import { CSSProperties, FC } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  GridItem,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverAnchor,
  Stack,
  Tooltip,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  UnorderedList,
  useBoolean,
} from '@chakra-ui/react';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { AddIcon, DragHandleIcon } from '@chakra-ui/icons';
import { MenuCategory } from 'types/menuCategory';
import { OriginalMenu } from 'types/menu';
import { TextWithTooltip } from 'components/molecules/TextWithTooltip/TextWithTooltip';
import { MenuCategoryNestedListItem } from '../MenuCategoryNestedListItem';
import { MenuCategoryNestedListEmpty } from '../MenuCategoryNestedListEmpty';

type Props = {
  /** メニューカテゴリー */
  item: MenuCategory;
  /** メニューカテゴリー追加 */
  handleClickAdd: () => void;
  /** メニューカテゴリーの行クリック */
  handleClickMenuCategoryRow: (menuCategory: MenuCategory) => void;
  /** 並び替え実行前の popover 表示状態チェック */
  checkOpenStateSortPopover: (id: MenuCategory['id']) => boolean;
  /** 並び替え実行 */
  handleSort: () => void;
  /** 並び替え実行キャンセル */
  handleCancelSort: () => void;
  /** カテゴリーごとのメニュー一覧を取得する */
  getNestedListItems: (categoryId: number) => OriginalMenu[];
  /** メニュー登録モーダルを開く */
  handleClickAddMenu: (categoryId: MenuCategory['id']) => void;
  /** メニュー詳細モーダルを開く */
  handleClickMenuRow: (menu: OriginalMenu) => void;

  /** メニュー並び替え実行前の処理 */
  handleDragStartMenus: (menus: OriginalMenu[]) => void;
  /** メニューの並び替え */
  handleDragEndMenus: (event: DragEndEvent) => void;
  /** メニュー並び替え実行前の popover 表示状態チェック */
  checkOpenStateSortPopoverMenu: (id: OriginalMenu['id']) => boolean;
  /** メニュー並び替え実行 */
  handleCancelSortMenu: () => void;
  /** メニュー並び替え実行キャンセル */
  handleSortMenu: () => void;
};

export const MenuCategoryListItem: FC<Props> = ({
  item,
  handleClickAdd,
  checkOpenStateSortPopover,
  handleCancelSort,
  handleSort,
  getNestedListItems,
  handleClickMenuCategoryRow,
  handleClickAddMenu,
  handleClickMenuRow,
  handleDragStartMenus,
  handleDragEndMenus,
  checkOpenStateSortPopoverMenu,
  handleCancelSortMenu,
  handleSortMenu,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    position: 'relative',
  };

  const [hover, setHover] = useBoolean();

  const menus = getNestedListItems(item.id);

  return (
    <AccordionItem
      key={item.id}
      ref={setNodeRef}
      style={style}
      onMouseEnter={setHover.on}
      onMouseLeave={setHover.off}
    >
      <AccordionButton as="a" background="gray.50" pl="16px" pr={0}>
        <Box
          m={0}
          w="100%"
          h="100%"
          textAlign="start"
          border="none"
          color="primaryList.item.color"
          cursor="pointer"
          position="relative"
        >
          <Grid
            templateColumns="0.5fr 1fr 2fr 1fr 3fr 2fr 3fr"
            gap="10px"
            justifyItems="start"
            alignItems="center"
          >
            <GridItem>
              <Stack spacing="4px" direction="row">
                <Tooltip label="カテゴリーを追加する">
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClickAdd();
                    }}
                    size="xs"
                    aria-label="カテゴリー追加"
                    icon={<AddIcon color="white" />}
                    colorScheme="blue"
                    visibility={hover ? 'visible' : 'hidden'}
                  />
                </Tooltip>
                <Tooltip label="カテゴリーを並び替える">
                  <IconButton
                    size="xs"
                    aria-label="カテゴリー並び替え"
                    icon={<DragHandleIcon />}
                    {...listeners}
                    {...attributes}
                    cursor={isDragging ? 'grabbing' : 'grab'}
                    visibility={hover ? 'visible' : 'hidden'}
                  />
                </Tooltip>
              </Stack>
            </GridItem>
            <GridItem>
              <TextWithTooltip text={item.id.toString()} />
            </GridItem>
            <GridItem>
              <Popover
                returnFocusOnClose={false}
                isOpen={checkOpenStateSortPopover(item.id)}
                placement="right"
                closeOnBlur={false}
                variant="darkShadow"
                strategy="fixed"
              >
                <PopoverAnchor>
                  <Button
                    variant="link"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClickMenuCategoryRow(item);
                    }}
                  >
                    <TextWithTooltip
                      text={item.name}
                      textMaxWidth="300px"
                      visibleTooltip
                      tooltipMaxWidth="600px"
                    />
                  </Button>
                </PopoverAnchor>
                <PopoverContent ml="16px">
                  <PopoverArrow />
                  <PopoverBody>並び替えを反映します</PopoverBody>
                  <PopoverFooter display="flex" justifyContent="flex-end">
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCancelSort();
                        }}
                      >
                        元に戻す
                      </Button>
                      <Button
                        colorScheme="blackAlpha"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSort();
                        }}
                      >
                        反映
                      </Button>
                    </ButtonGroup>
                  </PopoverFooter>
                </PopoverContent>
              </Popover>
            </GridItem>
            <GridItem />
            <GridItem />
            <GridItem />
            <GridItem />
          </Grid>
        </Box>
        <AccordionIcon position="absolute" top="10px" right="12px" />
      </AccordionButton>
      <AccordionPanel px="0" py="0">
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragStart={() => handleDragStartMenus(menus)}
          onDragEnd={handleDragEndMenus}
          collisionDetection={closestCenter}
        >
          <SortableContext items={menus} strategy={verticalListSortingStrategy}>
            <UnorderedList m="0">
              {menus.length ? (
                menus.map((menu) => (
                  <MenuCategoryNestedListItem
                    key={menu.id}
                    item={menu}
                    handleClickAddMenu={handleClickAddMenu}
                    handleClickMenuRow={handleClickMenuRow}
                    checkOpenStateSortPopoverMenu={
                      checkOpenStateSortPopoverMenu
                    }
                    handleCancelSortMenu={handleCancelSortMenu}
                    handleSortMenu={handleSortMenu}
                  />
                ))
              ) : (
                <MenuCategoryNestedListEmpty
                  categoryId={item.id}
                  handleClickAddMenu={handleClickAddMenu}
                />
              )}
            </UnorderedList>
          </SortableContext>
        </DndContext>
      </AccordionPanel>
    </AccordionItem>
  );
};
